import { dataTypes, inputTypes } from '@easybutton/constants'

import { indexOf } from '../arrays'
import { getAction } from '../../ducks/actions'
import { getTable } from '../../ducks/databases'
import { getColumn } from '../databases'

import {
  buildInputBinding,
  buildOutputBinding,
  buildColumnBinding,
  buildArrayItemBinding,
  buildFunctionBinding,
  FUNCTIONS,
} from '../bindings'

const getInputChildBindings = (state, input, dataType) => {
  const table = getTable(input.databaseId, input.table)(state)

  if (!table) return undefined

  const parentBinding = buildInputBinding(input)

  return getPropertyBindings(parentBinding, state, table.columns, dataType)
}

const getChildPropertyBindings = (parentBinding, state, column, dataType) => {
  switch (column.dataType) {
    case dataTypes.ARRAY: {
      const prototype = column.children && column.children[0]
      const binding = buildArrayItemBinding(parentBinding, 0)

      if (!prototype) return undefined

      const [result] = getPropertyBindings(
        binding,
        state,
        [prototype],
        dataType,
        true
      )

      if (!result) return result

      return [
        {
          ...result,
          label: 'First Item',
        },
      ]
    }

    case dataTypes.OBJECT:
      if (column.children) {
        return getPropertyBindings(
          parentBinding,
          state,
          column.children,
          dataType
        )
      }
  }
}

const getPropertyBindings = (
  parentBinding,
  state,
  columns,
  dataType,
  skipAddingColumn = false
) => {
  let filteredColumns = columns.filter(c => c.dataType === dataType)

  if (dataType && dataType.dataType === dataTypes.PRIMARY_KEY) {
    const { table: tableName } = dataType || {}

    filteredColumns = columns.filter(
      c => c.dataType === dataTypes.PRIMARY_KEY && c.table === tableName
    )
  }

  const resultColumns = columns.filter(
    c =>
      filteredColumns.includes(c) ||
      [dataTypes.ARRAY, dataTypes.OBJECT].includes(c.dataType)
  )

  return resultColumns.map(column => {
    const binding = skipAddingColumn
      ? parentBinding
      : buildColumnBinding(parentBinding, column.name)

    return {
      label: column.displayName || column.name,
      value: filteredColumns.includes(column) ? binding : undefined,
      children: getChildPropertyBindings(binding, state, column, dataType),
    }
  })
}

export const getInputBindings = (state, action, dataType) => {
  let inputs = action.details.inputs || []
  let results

  if (dataType && dataType.dataType === dataTypes.PRIMARY_KEY) {
    const { table: tableName, databaseId } = dataType

    const pkInputs = inputs.filter(
      input => input.type === inputTypes.PRIMARY_KEY
    )

    results = pkInputs.map(input => {
      const sameTable =
        input.table === tableName && input.databaseId === databaseId

      return {
        label: input.name,
        value: sameTable ? buildInputBinding(input) : undefined,
        children: !sameTable
          ? getInputChildBindings(state, input, dataType)
          : undefined,
      }
    })
  } else {
    results = inputs.map(input => {
      const isPrimaryKey = input.type === inputTypes.PRIMARY_KEY

      return {
        label: input.name,
        value: isPrimaryKey ? undefined : buildInputBinding(input),
        children: isPrimaryKey
          ? getInputChildBindings(state, input, dataType)
          : undefined,
      }
    })
  }

  results.push({
    label: '+ Add New Input...',
    value: 'addInput',
  })

  return [
    {
      label: 'Inputs',
      children: results,
      inline: true,
    },
  ]
}

const getOutputBindings = (state, dataType, action, stepId) => {
  if (!action) return []

  const { steps } = action.details
  const stepIndex = indexOf(steps, step => step.id === stepId)

  const createsBefore = steps
    .slice(0, stepIndex)
    .filter(step => step.operation === 'create')

  const options = createsBefore.map(step => {
    const binding = buildOutputBinding(step.id)
    const { table: tableName, databaseId } = step
    const table = getTable(databaseId, tableName)(state)

    let value

    if (
      dataType &&
      dataType.dataType === dataTypes.PRIMARY_KEY &&
      databaseId === dataType.databaseId &&
      tableName === dataType.table
    ) {
      value = binding
    }

    return {
      value,
      label: `New ${table.singularName || table.name}`,
      children: getPropertyBindings(binding, state, table.columns, dataType),
    }
  })

  if (options.length > 0) {
    return [
      {
        label: 'Previous Steps',
        children: options,
        inline: true,
      },
    ]
  }

  return []
}

export const getFunctionBindings = (state, dataType, actionId, stepId) => {
  return FUNCTIONS.map(func => {
    let result = {
      label: func.label,
    }

    if (func.hasArgument) {
      let children = getBindingsSub(state, dataType, actionId, stepId)

      traverseOptions(children, option => {
        if (option && option.value && option.value.type === 'binding') {
          option.label = `${func.label}(${option.label})`
        }

        if (option && option.value) {
          option.value = buildFunctionBinding(func, [option.value])
        }
      })

      result.children = children
    } else {
      result.value = buildFunctionBinding(func)
    }

    return result
  })
}

export const getBindingsSub = (state, dataType, actionId, stepId) => {
  let result = []

  let action = getAction(actionId)(state)

  if (!action) {
    return []
  }

  // Inputs
  result = result.concat(getInputBindings(state, action, dataType))

  // TODO: look at outputs from previous steps
  const outputBindings = getOutputBindings(state, dataType, action, stepId)

  if (result.length > 0 && outputBindings.length > 0) {
    result.push(null)
  }

  result = result.concat(outputBindings)

  return result
}

const getDataType = (column, databaseId) => {
  if (!column) return dataTypes.TEXT

  if (column.dataType === dataTypes.PRIMARY_KEY) {
    return {
      databaseId,
      dataType: column.dataType,
      table: column.table,
    }
  }

  return column.dataType
}

export const getBindings = (
  databaseId,
  tableName,
  columnName,
  actionId,
  stepId
) => state => {
  let table = getTable(databaseId, tableName)(state)
  let column = getColumn(table, columnName)
  let dataType = getDataType(column, databaseId)

  let bindings = getBindingsSub(state, dataType, actionId, stepId)

  bindings.push(null)

  bindings.push({
    label: 'More...',
    children: getFunctionBindings(state, dataType, actionId, stepId),
  })

  return bindings
}

export const traverseOptions = (options, func) => {
  for (let opt of options) {
    if (opt && opt.children) {
      traverseOptions(opt.children, func)
    }

    func(opt)
  }
}
